import React from "react";
import { useSelector } from "react-redux";
import { findKey } from "lodash";
import { getDrawingBox } from "reduxModules/ducks/ui";
import { getCoverageBoxes } from "reduxModules/calculations/roomCalculations";
import {
  calcExpansionCeilingMicPlacement,
  getExpansionCeilingIsFocused,
  resolveExpansionSpeakerBox,
  getExpansionSpeakerIsFocused,
} from "toolConstants/conferenceroomDesigner/logic";
import { RootState } from "types/redux/state";
import constants from "toolConstants/conferenceroomDesigner/constants";

const ACOUSTICS = constants.acoustics as string[];

const DashedRect: React.FC<{
  x: number; y: number; w: number; h: number; id: string; stroke?: string; fill?: string;
}> = ({ x, y, w, h, id, stroke = "orange", fill = "rgba(255, 165, 0, 0.08)" }) => (
  <g id={id}>
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      fill={fill}
      stroke={stroke}
      strokeWidth={2}
      strokeDasharray="6 4"
    />
  </g>
);

/**
 * Debug overlay: dashed outlines around the bar + expansion coverage boxes, so the actual
 * boxes the placement pass computes (not just the placed devices) are visible on the canvas.
 * Currently draws up to four, colour-coded, all in the same SVG coordinate space as the
 * device layers (getDrawingBox bbox/scale):
 *   - orange:  expansion CEILING MIC box (calcExpansionCeilingMicPlacement's expansionBox)
 *   - orange:  expansion TABLE MIC "box" (the table footprint itself)
 *   - cyan:    expansion CEILING/PENDANT SPEAKER box (resolveExpansionSpeakerBox's residual box)
 * - magenta: PRIMARY ceiling/pendant speaker's own coverage box (coverageBoxes.focused /
 *              full_room, the same box calculateSpeakerPlacement centers its grid in) — drawn for
 *              side-by-side comparison against the expansion box.
 * Mounted in DrawManager.tsx alongside SpeakerTargetMarkers.
 */
const MuiExpansionBox: React.FC = () => {
  const { bbox, scale } = useSelector(getDrawingBox);
  const area = useSelector((state: RootState) => {
    const { areas, activeTab } = state.container;
    return areas[activeTab];
  });
  const coverageBoxes = useSelector(getCoverageBoxes);

  // DEBUG ONLY: gated by the `debugCoverageBoxes` flag in constants.js, same pattern as
  // `debugSpeakerCoverageCircles` in MuiDevices.tsx. Flip the flag on to visualize the boxes
  // placement is computed against; leave it off for production.
  if (!constants.debugCoverageBoxes) return null;
  if (!area) return null;

  const {
    microphones, location, expansionMicType, expansionSpeakerType, expansionSpeakers,
    dimensions, table, ceilingHeight, speakers,
  } = area;

  const boxes: React.ReactNode[] = [];

  // PRIMARY ceiling/pendant speaker's own coverage box — the same box calculateSpeakerPlacement
  // centers its grid in. Drawn so it can be compared side-by-side with the expansion box below.
  {
    const primarySpkrEquipment = (constants.equipment.speakers as any)[speakers?.model];
    if (primarySpkrEquipment?.isCeilingSpkr || primarySpkrEquipment?.isPendantSpkr) {
      const coverageKey = findKey(speakers.coverage) as 'focused' | 'full_room' | undefined;
      const primaryBox = coverageKey ? coverageBoxes[coverageKey] : undefined;
      if (primaryBox) {
        boxes.push(
          <DashedRect
            key="coverage-box-primary-speaker"
            id="coverage-box-primary-speaker"
            stroke="#e91e63"
            fill="rgba(233, 30, 99, 0.06)"
            x={bbox.x0 + primaryBox.left * scale}
            y={bbox.y0 + (primaryBox.top ?? location.top) * scale}
            w={primaryBox.length * scale}
            h={(primaryBox.width ?? dimensions.width) * scale}
          />
        );
      }
    }
  }

  if (expansionMicType === 'ceiling') {
    const barModel = microphones?.model;
    const acousticIndex = ACOUSTICS.indexOf(area.acoustics ?? 'good');
    const isFocused = getExpansionCeilingIsFocused(area);
    const baseBox = isFocused ? coverageBoxes.focused : coverageBoxes.full_room;

    if (baseBox) {
      const result = calcExpansionCeilingMicPlacement({
        room: { left: location.left },
        baseBox,
        acousticIndex,
        isFocused,
        barModel,
      });
      if (result && 'expansionBox' in result) {
        const { expansionBox } = result as { expansionBox: { left: number; length: number; top?: number; width?: number } };
        boxes.push(
          <DashedRect
            key="expansion-box-ceiling-mic"
            id="expansion-box-ceiling-mic"
            x={bbox.x0 + expansionBox.left * scale}
            y={bbox.y0 + (expansionBox.top ?? location.top) * scale}
            w={expansionBox.length * scale}
            h={(expansionBox.width ?? dimensions.width) * scale}
          />
        );
      }
    }
  }

  if (expansionMicType === 'table' && table?.shape && table?.size) {
    const roomCenterX = location.left + dimensions.length / 2;
    const roomCenterY = location.top + dimensions.width / 2;
    const tableCenterX = roomCenterX + (table.transform?.x ?? 0);
    const tableCenterY = roomCenterY + (table.transform?.y ?? 0);
    const tw = table.size.length;
    const th = table.size.width;
    const tableLeft = tableCenterX - tw / 2;
    const tableFarEdge = tableCenterX + tw / 2;

    // The real placement (calcExpansionTableMicPlacement, DOM/path-based) samples the actual
    // table path and compares EUCLIDEAN distance from the bar's fixed wall position to each
    // point against the bar's own acoustics-indexed reach — expensive to replicate exactly in a
    // render function. This draws a centreline approximation instead (same simplification the
    // ceiling expansion box makes): project the bar's reach straight down the table's long axis
    // from its wall position, so the box at least SHRINKS/GROWS with acoustics like the real
    // uncovered arc does, rather than staying pinned to the table's static footprint regardless
    // of acoustics (the bug being fixed here).
    const barModel = microphones?.model;
    const acousticIndex = ACOUSTICS.indexOf(area.acoustics ?? 'good');
    const barReach = barModel ? (constants.equipment.microphones as any)[barModel]?.micData?.[acousticIndex] : undefined;
    const wallOffset = (constants.equipment as any)?.wallMountedBarOffset ?? 0.134;
    const barCoverageDepth = typeof barReach === 'number' ? location.left + wallOffset + barReach : tableLeft;
    const expansionLeft = Math.max(tableLeft, barCoverageDepth);
    const expansionLength = tableFarEdge - expansionLeft;

    if (expansionLength > 0) {
      boxes.push(
        <DashedRect
          key="expansion-box-table-mic"
          id="expansion-box-table-mic"
          x={bbox.x0 + expansionLeft * scale}
          y={bbox.y0 + (tableCenterY - th / 2) * scale}
          w={expansionLength * scale}
          h={th * scale}
        />
      );
    }
  }

  // Expansion SPEAKER coverage box — the residual area beyond the bar's own SPL-decay reach.
  // Mirrors computeExpansionSpeakerCoords' box resolution (roomCalculations.ts) exactly, so this
  // outline shows the ACTUAL box the placement pass uses, including its start (left edge) — the
  // thing under investigation.
  if (expansionSpeakerType === 'ceiling' || expansionSpeakerType === 'pendant') {
    const storedModel = expansionSpeakers?.model;
    const typeDefault = expansionSpeakerType === 'pendant' ? 'pendantspkr' : 'ceilingspkr';
    const expSpkrModel = storedModel && constants.equipment.speakers[storedModel] ? storedModel : typeDefault;
    const expSpkrEquipment = (constants.equipment.speakers as any)[expSpkrModel];

    if (expSpkrEquipment) {
      const talkerHeight = constants.area.talkerHeight;
      const expSpkrHeight = expansionSpeakers?.height
        ?? (typeof expSpkrEquipment.height === 'function' ? expSpkrEquipment.height(area) : ceilingHeight?.min);
      const expSpkrRadius = (expSpkrHeight - talkerHeight) * Math.tan((expSpkrEquipment.coverageAngle * Math.PI) / 180);

      const hasTable = Boolean(table?.shape);
      const expSpkrIsFocused = getExpansionSpeakerIsFocused(area);
      const spkrBaseBox = coverageBoxes[expSpkrIsFocused ? 'focused' : 'full_room'];
      const refDistance = hasTable
        ? Math.max(0.01, (dimensions.length - (table?.size?.length ?? 0)) / 2 + (table?.transform?.x ?? 0))
        : constants.barSpeakerNoTableRefDistance;

      if (spkrBaseBox && Number.isFinite(expSpkrRadius) && expSpkrRadius > 0) {
        const expansionBox = resolveExpansionSpeakerBox({
          room: { left: location.left },
          baseBox: spkrBaseBox,
          isFocused: expSpkrIsFocused,
          radius: expSpkrRadius,
          refDistance,
        });
        if (expansionBox) {
          boxes.push(
            <DashedRect
              key="expansion-box-speaker"
              id="expansion-box-speaker"
              stroke="#00bcd4"
              fill="rgba(0, 188, 212, 0.08)"
              x={bbox.x0 + expansionBox.left * scale}
              y={bbox.y0 + (expansionBox.top ?? location.top) * scale}
              w={expansionBox.length * scale}
              h={(expansionBox.width ?? dimensions.width) * scale}
            />
          );
        }
      }
    }
  }

  if (boxes.length === 0) return null;
  return <>{boxes}</>;
};

export default MuiExpansionBox;
